import { gql, type MutationHookOptions, useMutation } from "@apollo/client";

import type { KindEnum, OverallRatingEnum } from "@/pages/search/types";

export const PrivacyEnum = {
  FOLLOWING: "FOLLOWING",
  INNER_CIRCLE: "INNER_CIRCLE",
  PRIVATE: "PRIVATE",
  PUBLIC: "PUBLIC",
} as const;
export type PrivacyEnum = (typeof PrivacyEnum)[keyof typeof PrivacyEnum];

export interface ExperienceReviewType {
  components?: {
    id: string;
    response: {
      comment?: string;
      optionId?: string;
    };
  }[];
  id: string;
  images?: { id: string; imageKey: string }[];
  isContainingSpoilers: boolean;
  overallRating?: keyof typeof OverallRatingEnum;
  privacy: PrivacyEnum;
  privateNotes?: string;
  summary?: string;
}

export interface TaggedUserType {
  firstName: string;
  id: string;
  imageId?: string;
  lastName: string;
  username: string;
}

export interface EscapeType {
  companyName?: string;
  escapeTime?: string;
  game?: { id: string };
  gameDate?: string;
  gameName?: string;
  gameTime?: string;
  gameUrl?: string;
  id: string;
  isEscaped?: boolean;
  kind?: KindEnum;
  locationName?: string;
  myReview?: ExperienceReviewType;
  pictures?: { imageKey: string }[];
  taggedUsers: TaggedUserType[];
}

interface DataType {
  updateEscape: {
    escape?: EscapeType;
  };
}

interface EscapeInputType {
  companyName?: string;
  escapeTime?: string;
  game?: string;
  gameDate?: string;
  gameName?: string;
  gameTime?: string;
  gameUrl?: string;
  id?: string;
  isEscaped?: boolean;
  kind?: KindEnum;
  locationName?: string;
  pictures?: string[];
  taggedUsers?: string[];
}

interface ComponentResponseInputType {
  comment?: string;
  option?: string;
  question: string;
}

export interface ExperienceReviewInputType {
  components?: ComponentResponseInputType[];
  images?: string[];
  isContainingSpoilers?: boolean;
  overallRating?: keyof typeof OverallRatingEnum;
  privacy: PrivacyEnum;
  privateNotes?: string;
  summary?: string;
}

interface VariablesType {
  escape:
    | EscapeInputType
    | {
        game: string;
      };
  experienceReview?: ExperienceReviewInputType;
}

export const ESCAPE_UPDATE_FRAGMENT = gql`
  fragment ReviewUpdateFields on ExperienceReviewType {
    id
    components {
      id
      response {
        comment
        optionId
      }
    }
    images {
      id
      imageKey
    }
    isContainingSpoilers
    overallRating
    privacy
    privateNotes
    summary
  }

  fragment EscapeUpdateFields on EscapeType {
    id
    companyName
    escapeTime
    game {
      id
      overallRating
    }
    gameDate
    gameName
    gameTime
    gameUrl
    isEscaped
    kind
    locationName
    myReview {
      ...ReviewUpdateFields
    }
    pictures {
      imageKey
    }
    replicants
    taggedUsers {
      id
      firstName
      imageId
      lastName
      username
    }
  }
`;

const MUTATION = gql`
  ${ESCAPE_UPDATE_FRAGMENT}

  mutation UpdateEscape(
    $escape: EscapeMutationInput!
    $experienceReview: ExperienceReviewMutationInput
  ) {
    updateEscape(escape: $escape, experienceReview: $experienceReview) {
      escape {
        ...EscapeUpdateFields
      }
    }
  }
`;

export function convertEscapeToInput(escapeObj: EscapeType): VariablesType {
  const {
    id,
    companyName,
    escapeTime,
    gameDate,
    gameName,
    gameTime,
    gameUrl,
    isEscaped,
    kind,
    locationName,
    myReview,
  } = escapeObj;

  const variables: VariablesType = {
    escape: {
      companyName,
      escapeTime,
      game: escapeObj.game?.id,
      gameDate,
      gameName,
      gameTime,
      gameUrl,
      id,
      isEscaped,
      kind,
      locationName,
      pictures: escapeObj.pictures?.map(({ imageKey }) => imageKey),
      taggedUsers: escapeObj.taggedUsers.map(({ id }) => id),
    },
  };

  if (myReview) {
    const {
      isContainingSpoilers,
      overallRating,
      privacy,
      privateNotes,
      summary,
    } = myReview;

    variables.experienceReview = {
      components: myReview.components?.map((component) => ({
        comment: component.response.comment,
        option: component.response.optionId,
        question: component.id,
      })),
      images: myReview.images?.map(({ imageKey }) => imageKey),
      isContainingSpoilers,
      overallRating,
      privacy,
      privateNotes,
      summary,
    };
  }

  return variables;
}

export default function useUpdateEscapeMutation(
  props?: MutationHookOptions<DataType, VariablesType>
) {
  return useMutation<DataType, VariablesType>(MUTATION, props);
}
